// client/src/modules/NLPEntityDashboard.js
// Rebuilt Jul 2026 — ENQ-powered entity extraction, new design system
// Replaces Google NLP types with 15-class ENQ taxonomy
// Voronoi + bubble chart now coloured by ENQ class
// Styling matches VisibilityOverview / ContentHealth design system

import React, { useState, useRef, useEffect } from 'react';
import * as d3 from 'd3';

if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    const fl = document.createElement('link');
    fl.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
    fl.rel = 'stylesheet';
    document.head.appendChild(fl);
  }
}

// ── Design tokens (mirrors VisibilityOverview) ────────────────────────────────
const C = {
  primary:    '#39393a',
  teal:       '#048ba8',
  pine:       '#01796f',
  saffron:    '#e9c46a',
  sandy:      '#f4a261',
  sienna:     '#e76f51',
  lightGrey:  '#f3f4f6',
  midGrey:    '#9ca3af',
  borderGrey: '#e5e7eb',
  white:      '#ffffff',
};

// ENQ 15-class colour map
const ENQ_COLOURS = {
  BRAND:      C.teal,
  COMPETITOR: C.sienna,
  SERVICE:    C.pine,
  PRODUCT:    '#2563eb',
  REGULATOR:  '#7c3aed',
  CONCEPT:    C.saffron,
  TECHNOLOGY: '#0891b2',
  AUDIENCE:   C.sandy,
  GEOGRAPHY:  '#65a30d',
  CHANNEL:    '#db2777',
  FORMAT:     '#9333ea',
  METRIC:     '#0d9488',
  ROLE:       '#b45309',
  EVENT:      '#dc2626',
  OTHER:      C.midGrey,
};

const ENQ_ICONS = {
  BRAND:      'ti-building',
  COMPETITOR: 'ti-sword',
  SERVICE:    'ti-briefcase',
  PRODUCT:    'ti-package',
  REGULATOR:  'ti-gavel',
  CONCEPT:    'ti-bulb',
  TECHNOLOGY: 'ti-cpu',
  AUDIENCE:   'ti-users',
  GEOGRAPHY:  'ti-map-pin',
  CHANNEL:    'ti-antenna',
  FORMAT:     'ti-file-text',
  METRIC:     'ti-chart-bar',
  ROLE:       'ti-user',
  EVENT:      'ti-calendar',
  OTHER:      'ti-circle-dashed',
};

const FONT = "'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif";

function card(extra = {}) {
  return {
    backgroundColor: C.white,
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '20px 24px',
    marginBottom: '16px',
    fontFamily: FONT,
    ...extra,
  };
}

function sectionHeading(text, sub) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '15px', fontWeight: '700', color: C.primary }}>{text}</div>
      {sub && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

// ── ENQ class badge ───────────────────────────────────────────────────────────
function ClassBadge({ enqClass }) {
  const col = ENQ_COLOURS[enqClass] || C.midGrey;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
      backgroundColor: col + '18', color: col,
    }}>
      <i className={`ti ${ENQ_ICONS[enqClass] || 'ti-circle'}`} style={{ fontSize: '11px' }} />
      {enqClass}
    </span>
  );
}

// ── Salience bar ──────────────────────────────────────────────────────────────
function SalienceBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const col = pct >= 60 ? C.pine : pct >= 30 ? C.saffron : C.midGrey;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ flex: 1, height: '4px', background: C.lightGrey, borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: col, borderRadius: '2px' }} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: '600', color: C.primary, minWidth: '48px', textAlign: 'right' }}>
        {value.toFixed(4)}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const NLPEntityDashboard = ({ campaignId, userId, campaignName, urls, apiBaseUrl }) => {
  const [results, setResults]           = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState(null);
  const [selectedUrl, setSelectedUrl]   = useState(0);
  const [selectedClass, setSelectedClass] = useState('ALL');
  const [tooltip, setTooltip]           = useState({ show: false, x: 0, y: 0, data: null });
  const [activeDrawer, setActiveDrawer] = useState(null); // { entity }
  const svgRef = useRef(null);

  useEffect(() => {
    if (campaignId) loadLatestResults();
    else setIsLoading(false);
  }, [campaignId]);

  const loadLatestResults = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=nlp-entity-audit`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          const latest = data[0].result_data;
          setResults({ results: latest.urls || [], summary: latest.summary || {} });
          setSelectedUrl(0);
        }
      }
    } catch (err) {
      console.error('Error loading NLP entity audit:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const runAudit = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/modules/nlp-entity-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, userId, urls }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to run ENQ entity audit');
      await loadLatestResults();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const getCurrentEntities = () => {
    if (!results?.results || selectedUrl === null) return [];
    const urlResult = results.results[selectedUrl];
    if (!urlResult?.entities) return [];
    return urlResult.entities.map(e => ({
      entity:   e.name || e.display_name,
      type:     e.type || e.enq_class || 'OTHER',
      salience: e.salience || 0,
      mentions: e.mentions || 1,
      url:      urlResult.url,
    }));
  };

  const allEntities  = getCurrentEntities();
  const classes      = ['ALL', ...new Set(allEntities.map(e => e.type))].sort((a, b) => a === 'ALL' ? -1 : b === 'ALL' ? 1 : a.localeCompare(b));
  const filtered     = selectedClass === 'ALL' ? allEntities : allEntities.filter(e => e.type === selectedClass);
  const maxSalience  = filtered.length > 0 ? Math.max(...filtered.map(e => e.salience)) : 1;

  // class breakdown
  const classCounts = {};
  allEntities.forEach(e => { classCounts[e.type] = (classCounts[e.type] || 0) + 1; });
  const definedCount   = allEntities.filter(e => e.type !== 'OTHER').length;
  const classRate      = allEntities.length > 0 ? Math.round((definedCount / allEntities.length) * 100) : 0;

  // ── Exports ───────────────────────────────────────────────────────────────
  const downloadSVG = () => {
    if (!svgRef.current) return;
    const s = new XMLSerializer().serializeToString(svgRef.current);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([s], { type: 'image/svg+xml' }));
    a.download = `${campaignName}-entity-map.svg`;
    a.click();
  };

  const downloadPNG = () => {
    if (!svgRef.current) return;
    const s = new XMLSerializer().serializeToString(svgRef.current);
    const w = +svgRef.current.getAttribute('width') || 1000;
    const h = +svgRef.current.getAttribute('height') || 700;
    const canvas = document.createElement('canvas');
    canvas.width = w * 2; canvas.height = h * 2;
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${campaignName}-entity-map.png`;
        a.click();
      });
    };
    img.src = URL.createObjectURL(new Blob([s], { type: 'image/svg+xml' }));
  };

  const downloadCSV = () => {
    const rows = [['Rank', 'Entity', 'ENQ Class', 'Salience', 'Mentions']];
    [...filtered].sort((a, b) => b.salience - a.salience).forEach((e, i) => {
      rows.push([i + 1, `"${e.entity.replace(/"/g, '""')}"`, e.type, e.salience.toFixed(4), e.mentions]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${campaignName}-entities.csv`;
    a.click();
  };

  // ── Voronoi ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!svgRef.current || filtered.length === 0) return;

    const W = 900, H = 600;
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', W).attr('height', H)
      .attr('viewBox', `0 0 ${W} ${H}`)
      .style('max-width', '100%').style('height', 'auto');

    const nodes = filtered.map((d, i) => ({
      ...d,
      id:     i,
      radius: Math.max(12, Math.min(90, Math.sqrt(d.salience) * 220)),
    }));

    const sim = d3.forceSimulation(nodes)
      .force('charge', d3.forceManyBody().strength(4))
      .force('center', d3.forceCenter(W / 2, H / 2))
      .force('collision', d3.forceCollide().radius(d => d.radius + 3))
      .force('x', d3.forceX(W / 2).strength(0.08))
      .force('y', d3.forceY(H / 2).strength(0.08))
      .stop();

    for (let i = 0; i < 300; i++) sim.tick();

    const voronoi = d3.Delaunay.from(nodes, d => d.x, d => d.y).voronoi([0, 0, W, H]);

    // Background
    svg.append('rect').attr('width', W).attr('height', H).attr('fill', C.lightGrey).attr('rx', 8);

    // Voronoi cells
    svg.append('g').selectAll('path').data(nodes).join('path')
      .attr('d', (d, i) => voronoi.renderCell(i))
      .attr('fill', d => {
        const base = d3.color(ENQ_COLOURS[d.type] || C.midGrey);
        base.opacity = 0.12 + (d.salience / maxSalience) * 0.55;
        return base.toString();
      })
      .attr('stroke', d => ENQ_COLOURS[d.type] || C.midGrey)
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('stroke-width', 3);
        setTooltip({ show: true, x: event.pageX, y: event.pageY, data: d });
      })
      .on('mouseout', function() {
        d3.select(this).attr('stroke-width', 1.5);
        setTooltip({ show: false, x: 0, y: 0, data: null });
      })
      .on('click', (event, d) => setActiveDrawer({ entity: d }));

    // Bubbles
    svg.append('g').selectAll('circle').data(nodes).join('circle')
      .attr('cx', d => d.x).attr('cy', d => d.y).attr('r', d => d.radius)
      .attr('fill', d => ENQ_COLOURS[d.type] || C.midGrey)
      .attr('fill-opacity', 0.85)
      .attr('stroke', C.white).attr('stroke-width', 2)
      .style('pointer-events', 'none');

    // Labels
    svg.append('g').selectAll('text.name').data(nodes).join('text')
      .attr('class', 'name')
      .attr('x', d => d.x).attr('y', d => d.y - 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', d => Math.min(d.radius / 3.5, 13))
      .attr('font-weight', '600')
      .attr('font-family', FONT)
      .attr('fill', C.white)
      .style('pointer-events', 'none')
      .text(d => {
        const max = Math.floor(d.radius / 3.5);
        return d.entity.length > max ? d.entity.slice(0, max) + '…' : d.entity;
      });

    svg.append('g').selectAll('text.sal').data(nodes).join('text')
      .attr('class', 'sal')
      .attr('x', d => d.x).attr('y', d => d.y + 11)
      .attr('text-anchor', 'middle')
      .attr('font-size', d => Math.min(d.radius / 4.5, 11))
      .attr('font-family', FONT)
      .attr('fill', 'rgba(255,255,255,0.8)')
      .style('pointer-events', 'none')
      .text(d => d.salience.toFixed(3));

  }, [filtered, maxSalience]);

  // ── Loading / empty states ─────────────────────────────────────────────────
  if (isLoading) return (
    <div style={{ ...card(), textAlign: 'center', padding: '48px', fontFamily: FONT }}>
      <div style={{ width: '36px', height: '36px', border: `4px solid ${C.lightGrey}`, borderTop: `4px solid ${C.teal}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading entity analysis…</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ fontFamily: FONT }}>

      {/* ── Header ── */}
      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: C.primary, margin: '0 0 2px' }}>Entity Analysis</h2>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
              {campaignName} · ENQ 15-class taxonomy · {allEntities.length} entities
            </p>
          </div>
          <button
            onClick={runAudit}
            disabled={isProcessing}
            style={{
              padding: '9px 18px', borderRadius: '8px', border: 'none',
              backgroundColor: isProcessing ? C.midGrey : C.teal,
              color: C.white, fontSize: '13px', fontWeight: '600',
              fontFamily: FONT, cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            {isProcessing ? 'Analysing…' : 'Re-run analysis'}
          </button>
        </div>

        {error && (
          <div style={{ marginTop: '12px', padding: '12px 16px', backgroundColor: '#fee2e2', borderRadius: '8px', fontSize: '13px', color: '#dc2626' }}>
            {error}
          </div>
        )}

        {/* URL selector */}
        {results?.results?.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Select URL</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {results.results.map((r, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedUrl(i); setSelectedClass('ALL'); }}
                  style={{
                    padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontFamily: FONT,
                    border: `2px solid ${selectedUrl === i ? C.teal : C.borderGrey}`,
                    backgroundColor: selectedUrl === i ? C.teal + '12' : C.white,
                    color: selectedUrl === i ? C.teal : C.primary,
                    fontWeight: selectedUrl === i ? '600' : '400',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '2px' }}>{new URL(r.url).pathname.slice(0, 40) || '/'}</div>
                  <div style={{ fontSize: '12px' }}>{r.entities?.length || 0} entities</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!results && !isProcessing && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <i className="ti ti-analyze" style={{ fontSize: '40px', color: C.midGrey, display: 'block', marginBottom: '12px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: C.primary, marginBottom: '6px' }}>No entity data yet</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Click "Re-run analysis" to extract ENQ entities from {urls?.length || 0} URLs.</p>
          </div>
        )}
      </div>

      {allEntities.length > 0 && (
        <>
          {/* ── Summary tiles ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(110px, 160px))', gap: '10px', marginBottom: '16px', justifyContent: 'start' }}>
            {[
              { label: 'Total entities', value: allEntities.length, icon: 'ti-list', raw: true },
              { label: 'ENQ classified', value: `${classRate}%`, icon: 'ti-check', colour: classRate >= 70 ? C.pine : classRate >= 45 ? '#854F0B' : C.sienna },
              { label: 'ENQ classes', value: Object.keys(classCounts).length, icon: 'ti-tag', raw: true },
              { label: 'Avg salience', value: allEntities.length > 0 ? (allEntities.reduce((s, e) => s + e.salience, 0) / allEntities.length).toFixed(3) : '—', icon: 'ti-chart-bar', raw: true },
            ].map(tile => (
              <div key={tile.label} style={{ backgroundColor: C.white, border: `0.5px solid ${C.borderGrey}`, borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <i className={`ti ${tile.icon}`} style={{ fontSize: '15px', color: tile.colour || C.midGrey }} />
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>{tile.label}</span>
                </div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: tile.colour || C.primary }}>{tile.value}</div>
              </div>
            ))}
          </div>

          {/* ── Two-column main grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>

            {/* LEFT — Entity Map */}
            <div style={card({ padding: '20px 24px' })}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                {sectionHeading('Entity Map', 'Bubble size = salience · click any entity for detail')}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {[['SVG', downloadSVG], ['PNG', downloadPNG]].map(([label, fn]) => (
                    <button key={label} onClick={fn} style={{ padding: '5px 10px', fontSize: '12px', fontWeight: '600', color: C.primary, backgroundColor: C.white, border: `1px solid ${C.borderGrey}`, borderRadius: '6px', cursor: 'pointer', fontFamily: FONT }}>
                      ↑ {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Class filter */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                {classes.map(cls => (
                  <button
                    key={cls}
                    onClick={() => setSelectedClass(cls)}
                    style={{
                      padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                      fontFamily: FONT, cursor: 'pointer', border: 'none',
                      backgroundColor: selectedClass === cls
                        ? (ENQ_COLOURS[cls] || C.teal)
                        : C.lightGrey,
                      color: selectedClass === cls ? C.white : C.primary,
                    }}
                  >
                    {cls}
                    {cls !== 'ALL' && classCounts[cls] ? ` ${classCounts[cls]}` : ''}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <svg ref={svgRef} />
              </div>

              {/* ENQ legend */}
              <div style={{ marginTop: '14px', padding: '12px', backgroundColor: C.lightGrey, borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>ENQ Classes</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Object.entries(ENQ_COLOURS).map(([cls, col]) => (
                    <div key={cls} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: C.primary }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: col, flexShrink: 0 }} />
                      {cls}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — Entity table + Class breakdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Top entities table */}
              <div style={card({ padding: 0, overflow: 'hidden' })}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.lightGrey}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: C.primary }}>Top Entities by Salience</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Click a row for detail</div>
                  </div>
                  <button onClick={downloadCSV} style={{ padding: '5px 10px', fontSize: '12px', fontWeight: '600', color: C.primary, backgroundColor: C.white, border: `1px solid ${C.borderGrey}`, borderRadius: '6px', cursor: 'pointer', fontFamily: FONT }}>
                    ↑ CSV
                  </button>
                </div>

                {/* Sticky header */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: `2px solid ${C.borderGrey}` }}>
                      <th style={{ textAlign: 'left', padding: '10px 20px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>#</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Entity</th>
                      <th style={{ textAlign: 'right', padding: '10px 20px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Salience</th>
                    </tr>
                  </thead>
                </table>

                <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <tbody>
                      {[...filtered].sort((a, b) => b.salience - a.salience).slice(0, 20).map((e, idx) => (
                        <tr
                          key={idx}
                          style={{ borderBottom: `1px solid ${C.lightGrey}`, cursor: 'pointer', backgroundColor: C.white }}
                          onClick={() => setActiveDrawer({ entity: e })}
                          onMouseEnter={ev => ev.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={ev => ev.currentTarget.style.backgroundColor = C.white}
                        >
                          <td style={{ padding: '10px 20px', color: C.midGrey, fontWeight: '600', fontSize: '12px', width: '32px' }}>{idx + 1}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <div style={{ fontWeight: '500', color: C.primary, marginBottom: '3px' }}>{e.entity}</div>
                            <ClassBadge enqClass={e.type} />
                          </td>
                          <td style={{ padding: '10px 20px', width: '160px' }}>
                            <SalienceBar value={e.salience} max={maxSalience} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Class breakdown */}
              <div style={card({ padding: 0, overflow: 'hidden' })}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.lightGrey}` }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.primary }}>ENQ Class Breakdown</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {definedCount} of {allEntities.length} entities classified ({classRate}%)
                  </div>
                </div>
                <div style={{ padding: '12px 20px' }}>
                  {Object.entries(classCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cls, count]) => {
                      const pct = Math.round((count / allEntities.length) * 100);
                      const col = ENQ_COLOURS[cls] || C.midGrey;
                      return (
                        <div key={cls} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: `1px solid ${C.lightGrey}` }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: col, flexShrink: 0 }} />
                          <div style={{ flex: 1, fontSize: '13px', color: C.primary, fontWeight: '500' }}>{cls}</div>
                          <div style={{ width: '120px', height: '4px', background: C.lightGrey, borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{ width: pct + '%', height: '100%', background: col }} />
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', minWidth: '50px', textAlign: 'right' }}>{count} ({pct}%)</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Tooltip ── */}
      {tooltip.show && tooltip.data && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y + 12,
          backgroundColor: C.primary, color: C.white,
          padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          pointerEvents: 'none', zIndex: 1000, fontFamily: FONT, maxWidth: '220px',
        }}>
          <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '4px' }}>{tooltip.data.entity}</div>
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '2px' }}>
            <ClassBadge enqClass={tooltip.data.type} />
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>Salience: {tooltip.data.salience.toFixed(4)}</div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>Mentions: {tooltip.data.mentions}</div>
        </div>
      )}

      {/* ── Entity drawer ── */}
      {activeDrawer && (
        <>
          <div onClick={() => setActiveDrawer(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: 'min(420px, 92vw)', backgroundColor: C.white,
            boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: 1001,
            display: 'flex', flexDirection: 'column', fontFamily: FONT,
          }}>
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.borderGrey}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '17px', fontWeight: '700', color: C.primary, marginBottom: '6px' }}>{activeDrawer.entity.entity}</div>
                <ClassBadge enqClass={activeDrawer.entity.type} />
              </div>
              <button onClick={() => setActiveDrawer(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: C.midGrey, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
              {[
                ['ENQ Class', activeDrawer.entity.type],
                ['Salience', activeDrawer.entity.salience.toFixed(4)],
                ['Mentions', activeDrawer.entity.mentions],
                ['Source URL', activeDrawer.entity.url],
              ].map(([label, value]) => (
                <div key={label} style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</div>
                  <div style={{ fontSize: '13px', color: C.primary, wordBreak: 'break-all' }}>{value}</div>
                </div>
              ))}
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Salience</div>
                <SalienceBar value={activeDrawer.entity.salience} max={maxSalience} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NLPEntityDashboard;
